import React, {useCallback, useEffect, useState} from "react";
import {Col, Row, Spinner} from "react-bootstrap";
import DataMultiSelector from "../DataMultiSelector/DataMultiSelector";
import DateRangePicker from "../DateRangePicker/DateRangePicker";
import DonutChart from "../../DonutChart/DonutChart";
import StackedBarChart from "../../StackedBarChart/StackedBarChart";
import {ProjectColors} from "../../../Enums/Core/projectColors";
import {
    BAR_CHAR_ENTITIES_LEN_LIMIT, MAX_SELECTION_LIMIT
} from "../../../Constants/constants";
import Sort from "../SortComponent/Sort";
import {
    QUERY_GET_NUMBER_OF_LEAK_ALERTS_PIE
} from "../../../GraphQL/ReportGenerator/queries";
import {apolloClientReports} from "../../../GraphQL/config";
import {
    getLastMonthEndDate, getLastMonthStartDate, gqlDateFormat
} from "../Utils/utils";
import moment from "moment/moment";

export default function NumberOfLeakAlertsPie(props) {
    const STACKED_BAR_CHART_HEIGHT = 305;
    const DONUT_CHART_HEIGHT = 200;
    const DONUT_CHART_SIZE = '137%';
    const [response, setResponse] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const [pieChartData, setPieChartData] = useState();
    const [startDate, setStartDate] = useState(getLastMonthStartDate());
    const [endDate, setEndDate] = useState(getLastMonthEndDate());
    const [dateApplied, setDateApplied] = useState(false);
    const [entities, setEntities] = useState();
    const [selectedEntities, setSelectedEntities] = useState();
    const [chartData, setChartData] = useState();
    const [csvData, setCsvData] = useState([]);
    const [chartMinHeight, setChartMinHeight] = useState(200);
    const [chartPointWidth, setChartPointWidth] = useState(20);
    const [labelYPPosition, setLabelYPPosition] = useState(-20);
    const sortOptions = [{value: 'total', label: 'Total Alerts'}, {
        value: 'name', label: `${props.entityType} Name`
    }, {value: 'high', label: 'High Flow Alerts'}, {value: 'moderate', label: 'Moderate Flow Alerts'}, {
        value: 'low', label: 'Low Flow Alerts'
    },];
    const entityType = props.entityLabel;
    const [selectedSort, setSelectedSort] = useState(sortOptions[0].value);

    const calculateChartParams = (selectedEntities) => {
        if (!selectedEntities) {
            return
        }
        if (selectedEntities.length <= BAR_CHAR_ENTITIES_LEN_LIMIT) {
            setChartPointWidth(20)
            setChartMinHeight(70 * selectedEntities.length)
            setLabelYPPosition(-20)
        } else {
            setChartPointWidth(10)
            setChartMinHeight(40 * selectedEntities.length)
            setLabelYPPosition(-10)
        }
    }

    const getNumberOfLeakAlertsPieFromDB = () => {
        apolloClientReports.query({
            query: QUERY_GET_NUMBER_OF_LEAK_ALERTS_PIE, variables: {
                from: gqlDateFormat(startDate), to: gqlDateFormat(endDate), entityType: entityType
            }
        }).then(res => {
            let response = res?.data?.leakAlertEntityChart;
            if (!response) {
                console.error(`Data from getNumberOfLeakAlertsPieFromDB is empty`);
            } else {
                setDateApplied(false);
                setResponse(response);
                setIsLoading(false);
            }
        })
    }

    const buildCsvData = (responseData) => {
        let csvData = [];
        let headers = ['From', 'To', entityType, 'Total alerts', 'High', 'High (%)', 'Moderate', 'Moderate (%)', 'Low', 'Low (%)'];

        csvData.push(headers);

        responseData.forEach((entity, index) => {
            let row = [];
            if (index === 0) {
                row.push(moment(startDate).format('LL'), moment(endDate).format('LL'));
            } else {
                row.push('', '');
            }
            row.push(entity.label);

            let leakCounts = entity.severity.map((severityElement) => severityElement.value);
            let total = leakCounts.reduce((a, b) => a + b, 0);
            row.push(total, leakCounts[2], getPercentage(leakCounts[2] / total), leakCounts[1], getPercentage(leakCounts[1] / total), leakCounts[0], getPercentage(leakCounts[0] / total));
            csvData.push(row);
        });

        return csvData;
    }

    const getPercentage = (number) => {
        let integer = Math.floor(number * 100);
        let decimal = Math.floor((number - Math.floor(number)) * 10);
        return integer + decimal / 10;
    }


    const getTotalLeakAlerts = (array, index) => {
        return array
            ?.map((entity) => entity?.severity)
            .map((severity) => severity[index])
            .map((leakAlert) => leakAlert?.value ? leakAlert?.value : 0)
            .reduce((a, b) => a + b, 0)
    }

    useEffect(() => {
        calculateChartParams(selectedEntities)
    }, [selectedEntities, chartMinHeight, chartPointWidth])

    useEffect(() => {
        getNumberOfLeakAlertsPieFromDB();
    }, [])

    useEffect(() => {
        if (dateApplied) {
            setIsLoading(true);
            getNumberOfLeakAlertsPieFromDB();
        }
    }, [dateApplied]);


    useEffect(() => {
        let sortedEntities = response?.entities?.slice().sort((a, b) => parseFloat(a.severity[0].value + a.severity[1].value + a.severity[2].value) - parseFloat(b.severity[0].value + b.severity[1].value + b.severity[2].value)).reverse()
        setEntities(sortedEntities?.map((entity) => {
            return {value: entity.id, label: entity.name, severity: entity.severity}
        }));
    }, [response]);

    useEffect(() => {
        if (!selectedEntities) setSelectedEntities(entities?.slice(0, MAX_SELECTION_LIMIT));
    }, [entities]);

    useEffect(() => {
        setPieChartData([['Low', getTotalLeakAlerts(entities, 0)], ['Moderate', getTotalLeakAlerts(entities, 1)], ['High', getTotalLeakAlerts(entities, 2)]]);
    }, [chartData, entities]);

    useEffect(() => {
        switch (selectedSort) {
            case 'high':
                setSelectedEntities(selectedEntities?.sort((a, b) => parseFloat(a.severity[2].value) - parseFloat(b.severity[2].value)).reverse());
                break;
            case 'moderate':
                setSelectedEntities(selectedEntities?.sort((a, b) => parseFloat(a.severity[1].value) - parseFloat(b.severity[1].value)).reverse());
                break;
            case 'low':
                setSelectedEntities(selectedEntities?.sort((a, b) => parseFloat(a.severity[0].value) - parseFloat(b.severity[0].value)).reverse());
                break;
            case 'name':
                setSelectedEntities(selectedEntities?.sort((a, b) => a.label.localeCompare(b.label)));
                break;
            default:
                // Total
                setSelectedEntities(selectedEntities?.sort((a, b) => parseFloat(a.severity[0].value + a.severity[1].value + a.severity[2].value) - parseFloat(b.severity[0].value + b.severity[1].value + b.severity[2].value)).reverse());
                break;
        }
    }, [selectedSort, selectedEntities]);

    useEffect(() => {
        setChartData([{
            name: 'High Flow',
            data: selectedEntities?.map((entity) => entity?.severity[2]?.value ? entity.severity[2]?.value : 0),
            color: ProjectColors.DarkRed
        }, {
            name: 'Moderate Flow',
            data: selectedEntities?.map((entity) => entity?.severity[1]?.value ? entity.severity[1]?.value : 0),
            color: ProjectColors.Red
        }, {
            name: 'Low Flow',
            data: selectedEntities?.map((entity) => entity?.severity[0]?.value ? entity.severity[0]?.value : 0),
            color: ProjectColors.Orange
        }]);
    }, [selectedEntities, selectedSort]);


    useEffect(() => {
        if (selectedEntities) {
            setCsvData(buildCsvData(selectedEntities));
        }
    }, [selectedEntities])

    return (<>
        {(!pieChartData || !chartData || !selectedEntities) && <Spinner animation="border" variant="primary"/>}
        {pieChartData && chartData && entities && selectedEntities && <div className={'NumberOfLeakAlertsPie'}>
            <Row>
                <Col md={12}>
                    <p className={'card-title-main bold-title'}>{props.title}</p>
                </Col>
            </Row>
            <Row>
                <Col md={4}>
                    <DataMultiSelector
                        variant={props.variant}
                        setSelectedValues={setSelectedEntities}
                        entities={entities?.map((entity) => {
                            return {...entity, value: entity.value, label: entity.label}
                        })}
                        selectedValues={selectedEntities?.map((entity) => {
                            return {...entity, value: entity.value, label: entity.label}
                        })}/>
                </Col>
                <Col md={8} className={'DatePicker'}>
                    <DateRangePicker startDate={startDate} endDate={endDate} setStartDate={setStartDate}
                                     setEndDate={setEndDate} hasExportCsv
                                     csvData={csvData}
                                     setDateApplied={setDateApplied}
                                     title={`Number of leak alerts by ${entityType}s`}/>
                </Col>
            </Row>

            {isLoading && <Spinner animation="border" variant="primary"/>}
            {!isLoading && <>
                <div className={'mt-3'}/>
                <Sort sortOptions={sortOptions} value={selectedSort} setSelectedSort={setSelectedSort}/>
                <Row>
                    <Col md={4}>
                        <DonutChart height={DONUT_CHART_HEIGHT} data={pieChartData} size={DONUT_CHART_SIZE}/>
                    </Col>
                    <Col md={8} className={'NumberOfLeakAlerts'}>
                        <StackedBarChart
                            selectedEntities={selectedEntities}
                            isPieChart
                            labelYPPosition={labelYPPosition}
                            scrollbar
                            minScrollHeight={chartMinHeight}
                            chartPointWidth={chartPointWidth}
                            height={STACKED_BAR_CHART_HEIGHT}
                            data={chartData}
                            categories={selectedEntities?.map(e => {
                                return e.value !== null ? e.label : null
                            }).filter((a) => a)} horizontal={true}/>
                    </Col>
                </Row>
            </>}
        </div>}
    </>)
}
